import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { userClient } from '../../data/user';
import { setAuthCredentials } from '../../utils/auth-utils';
import { API_ENDPOINTS } from '../../data/api-endpoints';
import { HttpClient } from '../../data/http-client';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { t } from 'i18next';
import moment from 'moment';
import { Info, Edit } from 'react-feather';
import { FormattedDateRange } from '../../components/command/dateTime/formatDateRange';
import { DateTimeDisplay } from '../../components/command/dateTime/formatDateTime';
import { isSameDate, calculateDuration } from '../../utils/utils';
import "./extendRental.scss";
import product from '../product/product';
import { DepositIcon, Effect } from '../../assets/svgIcon/customIcon';
import Button from '../../components/command/Button/Button';

interface Product {
    id: number;
    productName: string;
    productUuid: string;
    productQuantity: number;
    productRentalValue: string;
    option1: string | null;
    option2: string | null;
    option3: string | null;
}

interface ExtendedRentalQuoteResponse {
    success: boolean;
    data: {
        customerUuid: string;
        quoteUuid: string;
        retailerUuid: string;
        storeUuid: string;
        rentalBookingStartDate: string;
        rentalBookingEndDate: string;
        paymentDueDate: string;
        products: Product[];
        rentalType: string;
        rentalDuration: string;
        totalQuoteValue: string;
        serviceCharge: string;
        totalDepositAmount: string;
    };
}

interface TermsResponse {
    success: string;
    pdf: string;
}

interface ExtendRentalResponse {
    success: boolean;
    data?: {
        orderReferenceId: string;
        // Add other fields from the response if needed
        [key: string]: any;
    };
    error?: string;
}

const ExtendRental: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [orderUuid, setOrderUuid] = useState<string>('');
    const [customerUuid, setCustomerUuid] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [quoteData, setQuoteData] = useState<any>(null);
    const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);
    const [accessToken, setAccessToken] = useState<string>('');
    const [rentalExtended, setRentalExtended] = useState<boolean>(false);
    const [orderReference, setOrderReference] = useState<string>('');

    // Add this function to handle the form submission
    const handleExtendRental = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!agreedToTerms) {
            toast.error('Please agree to the terms and conditions');
            return;
        }

        try {
            // Make the API call to extend the rental
            const response = await HttpClient.post<ExtendRentalResponse>(
                'extend-rental',
                {
                    orderUuid: orderUuid,
                    quoteUuid: quoteData.data?.quoteUuid
                },
                {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );

            if (response?.success) {
                setRentalExtended(true);
                setOrderReference(response.data?.orderReferenceId || '');
                // Clear the URL parameters
                window.history.replaceState({}, document.title, '/extend-rental');
            } else {
                throw new Error(response?.error || 'Failed to extend rental');
            }
        } catch (error: any) {
            console.error('Error extending rental:', error);
            toast.error('An error occurred while extending the rental');
        }
    };

    // Format currency as GBP
    const formatCurrency = (amount: string): string => {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
        }).format(parseFloat(amount || '0'));
    };

    // Function to fetch extended rental quote charges
    const fetchExtendedRentalQuote = async (orderUuid: string, accessToken: string) => {
        try {
            const response = await HttpClient.post<any>(  // Changed to 'any' since we'll handle the response directly
                API_ENDPOINTS.EXTENDED_RENTAL_QUOTE_CHARGES,
                { orderUuid },
                {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );
            return response; // Return the direct response
        } catch (error) {
            console.error('Error fetching extended rental quote:', error);
            throw error;
        }
    };

    useEffect(() => {
        const initialize = async () => {
            try {
                // First, authenticate the system user
                const systemLoginResponse = await userClient.initiateLogin({
                    username: process.env.REACT_APP_AUTH_SYSTEM_KEY || '',
                    password: process.env.REACT_APP_AUTH_SYSTEM_PASSWORD || '',
                });

                if (!systemLoginResponse?.success) {
                    throw new Error('Failed to authenticate system user');
                }

                const { access_token } = systemLoginResponse.data;
                setAccessToken(access_token);
                setAuthCredentials(access_token, systemLoginResponse.data.refresh_token);

                // Process URL parameters
                const searchString = location.search.substring(1);
                if (!searchString) {
                    throw new Error('Missing required parameters');
                }

                let orderId: string, customerId: string;

                if (searchString.includes(':')) {
                    [orderId, customerId] = searchString.split(':');
                } else {
                    const decodedString = atob(searchString);
                    [orderId, customerId] = decodedString.split(':');
                }

                if (!orderId || !customerId) {
                    throw new Error('Invalid URL format');
                }

                setOrderUuid(orderId);
                setCustomerUuid(customerId);

                // Fetch extended rental quote charges
                const quoteResponse = await fetchExtendedRentalQuote(orderId, access_token);
                setQuoteData(quoteResponse);

                // And replace it with:
                const response = await fetchExtendedRentalQuote(orderId, access_token);
                // Wrap the response in the expected format
                setQuoteData({
                    success: true,
                    data: response
                });

            } catch (error: any) {
                console.error('Error initializing:', error);
                toast.error(error.message || 'An error occurred while initializing');
                navigate('/not-found');
            } finally {
                setIsLoading(false);
            }
        };

        initialize();
    }, [location.search, navigate]);

    if (isLoading) {
        return <div className="loading-container">Loading rental details...</div>;
    }

    if (!quoteData) {
        return <div className="error-container">Failed to load rental details.</div>;
    }

    const {
        totalQuoteValue,
        paymentDueDate,
        serviceCharge,
        totalDepositAmount,
        rentalBookingStartDate,
        products
    } = quoteData.data || {};

    const handleTermsClick = async (e: React.MouseEvent) => {
        e.preventDefault();
        try {
            const response = await HttpClient.post<TermsResponse>('mobile/customer-terms-and-conditions', {
                storeUuid: quoteData.data?.storeUuid,
                retailerUuid: quoteData.data?.retailerUuid,
                customerUuid: quoteData.data?.customerUuid,
                quoteUuid: quoteData.data?.quoteUuid,
                termsAndConditionsAccepted: true
            });

            if (response?.success) {
                // Open the PDF in a new tab
                if (response.pdf) {
                    window.open(response.pdf, '_blank');
                }
            }
        } catch (error) {
        }
    };

    function placeOrder(event: React.MouseEvent<HTMLButtonElement>): void {
        handleExtendRental(event as any);
    }

    if (rentalExtended) {
        return (
     
                        <div className="password-reset-success">
                                 <div className="success-container">
                               <div className="success-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
        </div>
                                <h1 className="mb-3">Thank You!</h1>
                                <p className=" mb-4">Your rental has been successfully extended.</p>
                                {orderReference && (
                                    <div className="order-reference mt-4 p-3 bg-light rounded">
                                        <p className="mb-1">Order Reference:</p>
                                        <h4 className="text-primary" style={{ color: '#00989E' }}>{orderReference}</h4>
                                    </div>
                                )}
                            </div>
                        </div>
                   




        );
    }

    return (
        <div className="extend-rental">
            <div className='section-space'>
                <div className='container'>
                    <div className='row'>
                        <div className='offset-xxl-0 col-xxl-8 offset-xl-0 col-xl-7 offset-md-1 col-md-10'>

                            <h2 className='extent-heading mb-3'>Extend into a Flexi Monthly Rolling rental plan</h2>

                            <div className='row'>
                                <div className='col-md-6 mb-3'>
                                    <div className='flexi-rental-option h-100'>
                                        <div>
                                            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M1.6665 6.66667V16.6667H11.6665" stroke="#00989E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                <path d="M5.78343 24.8987C6.86409 27.966 8.91232 30.599 11.6195 32.401C14.3267 34.203 17.5462 35.0764 20.793 34.8895C24.0397 34.7027 27.1377 33.4657 29.6203 31.365C32.1029 29.2643 33.8355 26.4137 34.5571 23.2427C35.2787 20.0717 34.9503 16.752 33.6211 13.7839C32.292 10.8158 30.0343 8.36007 27.1882 6.78668C24.342 5.2133 21.0616 4.60752 17.8412 5.0606C14.6209 5.51369 11.635 7.00111 9.33343 9.29874L1.6001 16.5654" stroke="#00989E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                <path d="M19.6572 12.9143V21.6L25.4477 24.4952" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                            </svg>
                                            <p>Roll into a Flexi rental each month​</p>
                                        </div>

                                    </div>
                                </div>
                                <div className='col-md-6 mb-3'>
                                    <div className='flexi-rental-option h-100'>
                                        <div>
                                            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <g clip-path="url(#clip0_1_129395)">
                                                    <path d="M20.0002 36.6667C29.2049 36.6667 36.6668 29.2047 36.6668 20C36.6668 10.7952 29.2049 3.33333 20.0002 3.33333C10.7954 3.33333 3.3335 10.7952 3.3335 20C3.3335 29.2047 10.7954 36.6667 20.0002 36.6667Z" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                    <path d="M20 10V20L26.6667 23.3333" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                    <path d="M10.6948 38.9343C16.2177 38.9343 20.6948 34.4571 20.6948 28.9343C20.6948 23.4114 16.2177 18.9343 10.6948 18.9343C5.17198 18.9343 0.694824 23.4114 0.694824 28.9343C0.694824 34.4571 5.17198 38.9343 10.6948 38.9343Z" fill="#00989E" />
                                                    <path d="M13.6948 25.9343L7.69482 31.9343" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                    <path d="M7.69482 25.9343L13.6948 31.9343" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                </g>
                                                <defs>
                                                    <clipPath id="clip0_1_129395">
                                                        <rect width="40" height="40" fill="white" />
                                                    </clipPath>
                                                </defs>
                                            </svg>

                                            <p>Cancel any time ​</p>
                                        </div>

                                    </div>
                                </div>
                            </div>

                            {/*  */}
                            <div
                                className="cart-product-date ">
                                <h4 className="estimated-date">
                                    RENEWAL DATE:
                                    <OverlayTrigger
                                        placement="auto"
                                        overlay={
                                            <Tooltip
                                                id={`tooltip-e-date`}
                                                style={{
                                                    position: "fixed",
                                                }}
                                            >
                                                {t(
                                                    "estimated-dates-tooltip"
                                                )}
                                            </Tooltip>
                                        }
                                        popperConfig={{
                                            modifiers: [
                                                {
                                                    name: "preventOverflow",
                                                    options: {
                                                        boundary:
                                                            "window", // Ensure the tooltip stays within the window boundary
                                                    },
                                                },
                                                {
                                                    name: "flip",
                                                    options: {
                                                        fallbackPlacements:
                                                            [
                                                                "top",
                                                                "bottom",
                                                                "left",
                                                                "right",
                                                            ], // Provide fallback placements
                                                    },
                                                },
                                            ],
                                        }}
                                    >
                                        <Info className="info-icon" />
                                    </OverlayTrigger>
                                </h4>

                                <div className="selected-date">
                                    <div className="selected-date-box">
                                        <h5 className="product-date">
                                            {/* Hourly Base */}
                                            {new Date(rentalBookingStartDate).toLocaleDateString('en-GB', {
                                                day: 'numeric',
                                                month: 'long',
                                                year: 'numeric'
                                            })}
                                        </h5>
                                        <span className="date-duration">Duration : Flexible</span>
                                    </div>

                                </div>

                            </div>
                            {/*  */}

                            <div className="cart-product-section product-box-section">
                                <div className="cart-product-title">
                                    <h4>{t("products-details")}</h4>
                                    <h4>{t("total")}</h4>
                                </div>


                                {quoteData?.data?.products?.map((product: Product, index: number) => (
                                    <div className="cart-product-details-boxs" key={`product-${index}`}>
                                        <div className="cart-product-details">
                                            <div className="d-flex align-items-center justify-content-between mb-1">
                                                <a className="c-product-title" href="">
                                                    {product?.productName} x{product?.productQuantity}
                                                </a>
                                                <h5 className="product-price monthly-product-price">
                                                    {formatCurrency(product?.productRentalValue || '0')} per month
                                                </h5>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                            </div>
                        </div>


                        <div className="offset-xxl-0 col-xxl-4 offset-xl-0 col-xl-5 offset-md-1 col-md-10 mb-5">

                            <div className="order-summary-position">
                                <div className="effect-add-section cart-page-effect">
                                    <Effect className="effect-img" />

                                    <div className="cart-product-section delivery-section">
                                        <div className="cart-product-title order-summary">
                                            <h4>
                                                {t("order-summary")}
                                            </h4>
                                        </div>

                                        <div className="cart-value">

                                            <h5 className="cart-title">
                                                Renticy{" "}
                                                {t(
                                                    "renticy-service-charge"
                                                )}
                                                <OverlayTrigger
                                                    placement="bottom"
                                                    overlay={
                                                        <Tooltip
                                                            id={`tooltip-service`}
                                                            style={{
                                                                position:
                                                                    "fixed",
                                                            }}
                                                        >
                                                            <div dangerouslySetInnerHTML={{ __html: t("renticy-service-charge-tooltip") }} />
                                                        </Tooltip>
                                                    }
                                                >
                                                    <Info
                                                        className="info-icon ms-2"
                                                        size={
                                                            18
                                                        }
                                                    />
                                                </OverlayTrigger>
                                            </h5>
                                            <h5 className="product-price d-flex monthly-product-price">
                                                {formatCurrency(serviceCharge)} per month

                                            </h5>


                                        </div>


                                        <div className="cart-value total-cart">
                                            <h5 className="cart-title">
                                                Total monthly cost
                                            </h5>
                                            <h5 className="product-price cart-price">
                                                {formatCurrency(totalQuoteValue)} per month
                                            </h5>
                                        </div>


                                        <div className="cart-product-section refundable-deposit divider-top">
                                            <div className="refundable-title">
                                                <DepositIcon className="me-2 mb-1 img-fluid info-icon" />
                                                <h5 className="cart-title">
                                                    {t(
                                                        "refundable-deposit"
                                                    )}
                                                    <span>
                                                        {t("what-is-this")}
                                                        <OverlayTrigger
                                                            placement="bottom"
                                                            overlay={
                                                                <Tooltip
                                                                    id={`tooltip-rd`}
                                                                    style={{
                                                                        position:
                                                                            "fixed",
                                                                    }}
                                                                >
                                                                    {t(
                                                                        "refundable-deposit-tooltip"
                                                                    )}
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <Info
                                                                className="info-icon ms-1"
                                                                size={18}
                                                            />
                                                        </OverlayTrigger>
                                                    </span>
                                                </h5>
                                            </div>
                                            <h5 className="product-price cart-price">
                                                {/* {formatCurrency(totalDepositAmount)} */}

                                             <p className='rolledover-capsule'>   Rolled Over</p>
                                             <p className='rolledover-helpingtext'>Nothing to pay today​</p>
                                            </h5>
                                        </div>

                                        <div className="cart-value divider-top">
                                            <h5 className="cart-title">
                                                Monthly Payment Date
                                            </h5>
                                            <h5 className="product-price cart-price">
                                                {new Date(paymentDueDate).getDate()}th
                                            </h5>
                                        </div>

                                        <div className="cart-value ">
                                            <h5 className="cart-title">
                                                Next payment date
                                            </h5>
                                            <h5 className="product-price cart-price">
                                                {new Date(paymentDueDate).toLocaleDateString('en-GB', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}
                                            </h5>
                                        </div>


                                    </div>




                                    <div className="place-order-btn">
                                        <div className="terms-section mb-3">
                                            <label className="checkbox-container">
                                                <input
                                                    type="checkbox"
                                                    className='me-2'
                                                    checked={agreedToTerms}
                                                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                                                    required
                                                />
                                                <span className="checkmark"></span>
                                                I agree to the <a href="#" onClick={handleTermsClick} target="_blank" rel="noopener noreferrer">Terms & Conditions</a> and understand my responsibilities
                                            </label>
                                        </div>
                                        <Button
                                            btnText="Extend Rental"
                                            onClick={placeOrder}
                                            className={`extend-button ${!agreedToTerms ? 'disabled' : ''}`}
                                            disabled={!agreedToTerms}

                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>















    );
};

export default ExtendRental;